import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatStudyDate } from './studyDateFormat';
import { journeyStatusLabel } from './studyJourneyLabels';
import type { JourneyNode } from './studyYearApi';
import { StoryStar } from './StudyJourneyPortfolio';
import type { JourneyStory } from './useJourneyStory';
import {
  activitiesLabel,
  formatShortDate,
  groupByWeek,
  helpDescription,
} from './studyTermProgress';

/** The latest weeks open by default; older weeks start collapsed (PRD §6.1 item 4). */
export const OPEN_WEEKS_BY_DEFAULT = 2;
const OPEN_WEEKS_PREFIX = 'airbotix.study.journey-open-weeks.v1';

const KIND_LABELS: Record<JourneyNode['kind'], string> = {
  session: 'Study Session',
  task: 'Task',
  assessment: 'Assessment',
  reflection: 'Reflection',
  working: 'Saved working',
};

function readOpenWeeks(key: string): string[] | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) && parsed.every((v) => typeof v === 'string') ? parsed : null;
  } catch {
    return null;
  }
}

function writeOpenWeeks(key: string, weeks: string[]) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(weeks));
  } catch {
    // The page still works; only the remembered open weeks are lost.
  }
}

/**
 * One Term's records grouped by week, newest first. Which weeks are open is kept for this tab, so
 * Back from a record returns to the same view.
 */
export function StudyJourneyRecords({
  scopeKey,
  items,
  timezone,
  termStartsOn,
  subjects,
  story,
  focusReflection,
}: {
  scopeKey: string;
  items: JourneyNode[];
  timezone: string;
  termStartsOn: string | null;
  subjects: Map<string, string>;
  story: JourneyStory;
  focusReflection: boolean;
}) {
  const storageKey = `${OPEN_WEEKS_PREFIX}:${scopeKey}`;
  const groups = useMemo(
    () => groupByWeek(items, termStartsOn, timezone),
    [items, termStartsOn, timezone],
  );
  const reflectionKey = items.find((item) => item.kind === 'reflection')?.key;
  const reflectionWeek = groups.find((group) =>
    group.items.some((item) => item.key === reflectionKey),
  )?.key;
  const [openWeeks, setOpenWeeks] = useState<string[] | null>(() => readOpenWeeks(storageKey));
  const isOpen = (key: string, index: number) =>
    (focusReflection && key === reflectionWeek) ||
    (openWeeks ? openWeeks.includes(key) : index < OPEN_WEEKS_BY_DEFAULT);

  useEffect(() => {
    if (!focusReflection || !reflectionKey) return;
    document.getElementById('reflection')?.scrollIntoView?.({ block: 'center' });
  }, [focusReflection, reflectionKey]);

  const toggle = (key: string, index: number, open: boolean) => {
    const current = groups.filter((group, i) => isOpen(group.key, i)).map((group) => group.key);
    if (open === isOpen(key, index)) return;
    const next = open ? [...current, key] : current.filter((week) => week !== key);
    setOpenWeeks(next);
    writeOpenWeeks(storageKey, next);
  };

  return (
    <div className="mt-6 space-y-4">
      {groups.map((group, index) => (
        <details
          key={group.key}
          open={isOpen(group.key, index)}
          onToggle={(event) => toggle(group.key, index, event.currentTarget.open)}
          className="rounded-3xl border border-hairline bg-canvas-pure"
        >
          <summary className="min-h-11 cursor-pointer content-center px-5 py-3 text-base font-extrabold text-ink">
            {group.label} · {activitiesLabel(group.items.length)}
          </summary>
          <ol className="space-y-3 border-t border-hairline p-4 sm:p-5">
            {group.items.map((item) => (
              <li
                key={item.key}
                id={item.key === reflectionKey ? 'reflection' : undefined}
                className="flex items-start justify-between gap-3 rounded-2xl bg-surface p-4"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-slate2">
                    <span>{KIND_LABELS[item.kind]}</span>
                    {journeyStatusLabel(item.status) && (
                      <span className="rounded-full bg-canvas-pure px-2 py-0.5">
                        {journeyStatusLabel(item.status)}
                      </span>
                    )}
                    <time dateTime={item.at}>{formatStudyDate(item.at, { timezone })}</time>
                  </div>
                  {item.href ? (
                    <Link
                      to={item.href}
                      className="mt-2 block break-words text-base font-extrabold text-ink hover:underline"
                    >
                      {item.title}
                    </Link>
                  ) : (
                    <p className="mt-2 break-words text-base font-extrabold text-ink">
                      {item.title}
                      <span className="ml-2 text-xs font-semibold text-slate2">
                        This item was deleted
                      </span>
                    </p>
                  )}
                  {item.reflection && (
                    <p className="mt-2 whitespace-pre-wrap text-sm text-ink">{item.reflection}</p>
                  )}
                  <p className="mt-1 text-sm text-ink-soft">
                    {[
                      subjects.get(item.subject_key ?? '') ?? 'Personal study',
                      item.kind === 'session' ? helpDescription(item.highest_hint_level) : '',
                      item.due_on ? `Due ${formatShortDate(item.due_on)}` : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <StoryStar story={story} recordKey={item.key} title={item.title} />
              </li>
            ))}
          </ol>
        </details>
      ))}
    </div>
  );
}
